// The Lobby, laid out in categories (owner's request, October 2026: "catégoriser les fonctionnalités,
// comme un architecte de jeux vidéo"): a header with the player's chip, the language switch and the
// settings; five tabs — Jouer, Table, Profil, Social, Rang (components/lobby/) — in a bar at the
// bottom of a phone, in the header of a large screen. This page holds the data (the lists polled, the
// player's rank, the invitations) and the actions; the tab is kept in the URL (?tab=).
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FlaskConical } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "../lib/notify";
import { t, trServer, useLang } from "../lib/i18n";
import { BrandBackground } from "../components/BrandBackground";
import { TutorialDialog, useTutorialSeen } from "../components/tutorial/TutorialDialog";
import { LobbyHeader } from "../components/lobby/LobbyHeader";
import { LOBBY_TABS, LobbyTabBar } from "../components/lobby/LobbyTabBar";
import { PlayTab } from "../components/lobby/PlayTab";
import { RankTab } from "../components/lobby/RankTab";
import { SocialTab } from "../components/lobby/SocialTab";
import { ProfileTab } from "../components/lobby/ProfileTab";
import { TableTab } from "../components/lobby/TableTab";
import { SettingsSheet } from "../components/lobby/SettingsSheet";
import { CreateRoomDialog } from "../components/lobby/CreateRoomDialog";
import { useMyRank } from "../components/rank/RankViews";
import { useInvites } from "../components/players/invites";
import { useTopBanner } from "../lib/ads";
import { matStyle, useMyTableTheme } from "../lib/tableThemes";

const DEBUG_SCENARIOS = process.env.REACT_APP_DEBUG_SCENARIOS === "true";
const TAB_IDS = LOBBY_TABS.map((x) => x.id);
const TAPIS_SEEN = "rami_tapis_seen";
const SOLO_LEVEL = "rami_solo_level";

const readLocal = (key, fallback) => {
  try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
};
const writeLocal = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* private mode */ }
};

// Copie un texte pas encore connu (promesse). L'écriture doit être lancée dans le geste
// utilisateur : Safari/iOS refuse le presse-papiers après un await.
async function copyDeferredText(textPromise) {
  try {
    if (window.ClipboardItem && navigator.clipboard?.write) {
      const blob = textPromise.then((t) => new Blob([t], { type: "text/plain" }));
      await navigator.clipboard.write([new ClipboardItem({ "text/plain": blob })]);
      return true;
    }
  } catch {}
  try {
    await navigator.clipboard.writeText(await textPromise);
    return true;
  } catch {
    return false;
  }
}

export default function Lobby() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  useLang();
  // AdMob banner over the top of the page (Android / iOS apps only): 0 elsewhere. The same
  // banner as the game's: opening a game from here keeps it on screen (lib/ads.js).
  const bannerHeight = useTopBanner();
  const tableThemeId = useMyTableTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TAB_IDS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "play";
  const goTab = useCallback((id) => {
    setSearchParams(id === "play" ? {} : { tab: id }, { replace: true });
    window.scrollTo({ top: 0 });
  }, [setSearchParams]);

  const [rooms, setRooms] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tutoOpen, setTutoOpen] = useState(false);
  const [tutoSeen, markTutoSeen] = useTutorialSeen();
  const [tapisSeen, setTapisSeen] = useState(() => readLocal(TAPIS_SEEN, "0") === "1");
  const [scenarios, setScenarios] = useState([]);
  const [debugLoading, setDebugLoading] = useState(null);
  const [activeGames, setActiveGames] = useState([]);
  const [quittingGame, setQuittingGame] = useState(null);
  const quittingRef = useRef(null); // ref so polling closure can read current value
  const [onlineRooms, setOnlineRooms] = useState([]);
  const [matchmakingLoading, setMatchmakingLoading] = useState(false);
  const [rank] = useMyRank();
  const { invites, answer, acting, errors } = useInvites();

  // Penalty toggles — when OFF, the corresponding penalty is set to 0
  // (= règle désactivée), keeping all backend logic intact.
  const [toggles, setToggles] = useState({
    standard: true,
    joker: true,
    continuer: true,
    usefulDiscard: true,
    nefrechFausse: true,
    pureSequence: true,
  });

  const [form, setForm] = useState(() => ({
    num_players: 2,
    threshold_x: 71,
    elim_score_y: 501,
    num_jokers: 4,
    standard_penalty: 50,
    joker_in_hand_penalty: 50,
    useful_discard_penalty: 50,
    continuer_penalty: 50,
    nefrech_fausse_penalty: 50,
    num_bots: 0,
    // The training tile's bot level, remembered on the device (also the level of the bots a table starts with).
    bot_difficulty: readLocal(SOLO_LEVEL, "legende"),
    room_type: "private",
  }));
  const setSoloLevel = (level) => {
    setForm((f) => ({ ...f, bot_difficulty: level }));
    writeLocal(SOLO_LEVEL, level);
  };

  // Effective penalty values sent to the backend (toggles disable each rule).
  const effectivePenalties = () => ({
    standard_penalty: toggles.standard ? form.standard_penalty : 0,
    joker_in_hand_penalty: toggles.joker ? form.joker_in_hand_penalty : 0,
    useful_discard_penalty: toggles.usefulDiscard ? form.useful_discard_penalty : 0,
    continuer_penalty: toggles.continuer ? form.continuer_penalty : 0,
    nefrech_fausse_penalty_enabled: toggles.nefrechFausse,
    nefrech_fausse_penalty: form.nefrech_fausse_penalty,
  });

  const fetchRooms = useCallback(async () => {
    try {
      const { data } = await api.get("/rooms");
      setRooms(data.rooms || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/");
      return;
    }
    fetchRooms();
    const id = setInterval(fetchRooms, 5000);
    return () => clearInterval(id);
  }, [user, loading, navigate, fetchRooms]);

  useEffect(() => {
    if (!user) return;
    // Skip poll while a quit is in flight — prevents re-adding the game between
    // clicking "Quitter" and the API response arriving.
    const check = () => {
      if (quittingRef.current) return;
      api.get("/active-rooms").then(({ data }) => setActiveGames(data.rooms || [])).catch(() => {});
    };
    check();
    const id = setInterval(check, 5000);
    return () => clearInterval(id);
  }, [user]);

  const fetchOnline = useCallback(
    () => api.get("/rooms/online").then(({ data }) => setOnlineRooms(data.rooms || [])).catch(() => {}),
    []
  );
  useEffect(() => {
    if (!user) return;
    fetchOnline();
    const id = setInterval(fetchOnline, 4000);
    return () => clearInterval(id);
  }, [user, fetchOnline]);

  // The refresh button of the open tables: both lists at once. It turns only after a tap, not
  // at every poll, and long enough to be seen.
  const [refreshing, setRefreshing] = useState(false);
  const refreshLists = async () => {
    setRefreshing(true);
    await Promise.all([fetchRooms(), fetchOnline(), new Promise((r) => setTimeout(r, 500))]);
    setRefreshing(false);
  };

  const openCreateDialog = () => {
    setForm((f) => ({ ...f, room_type: "private", num_bots: 0 }));
    setCreateOpen(true);
  };

  const handlePlayOnline = async () => {
    setMatchmakingLoading(true);
    try {
      const { data } = await api.post("/matchmaking");
      navigate(`/room/${data.room_id}`);
    } catch (e) {
      toast.error(trServer(e?.response?.data?.detail) || t("lobby.toast.matchmaking"));
    } finally {
      setMatchmakingLoading(false);
    }
  };

  const fetchScenarios = () => {
    if (!DEBUG_SCENARIOS) return;
    api.get("/debug/scenarios").then(({ data }) => setScenarios(data.scenarios || [])).catch(() => {});
  };

  useEffect(() => {
    fetchScenarios();
    const onFocus = () => fetchScenarios();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDebugScenario = async (scenarioId) => {
    setDebugLoading(scenarioId);
    try {
      const { data } = await api.post("/debug/rooms", { scenario: scenarioId });
      toast.success(`Scénario lancé — ${data.hint}`);
      navigate(`/game/${data.room_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur debug");
    } finally {
      setDebugLoading(null);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-300">
        {t("common.loading")}
      </div>
    );
  }

  const handleCreate = async () => {
    const request = api.post("/rooms", { ...form, ...effectivePenalties(), require_pure_sequence: toggles.pureSequence });
    // The room code is copied at once, for the player to send to friends (owner's choice: the code,
    // not the invite link, on the website and in the apps).
    const copying = copyDeferredText(request.then(({ data }) => data.room.code));
    try {
      const { data } = await request;
      setCreateOpen(false);
      navigate(`/room/${data.room.room_id}`);
      if (await copying) toast.success(t("lobby.codeCopied", { code: data.room.code }));
    } catch (e) {
      toast.error(trServer(e?.response?.data?.detail) || t("lobby.toast.create"));
    }
  };

  const handleJoin = async (roomId) => {
    try {
      await api.post(`/rooms/${roomId}/join`);
      navigate(`/room/${roomId}`);
    } catch (e) {
      toast.error(trServer(e?.response?.data?.detail) || t("common.error"));
    }
  };

  const handleQuitGame = async (roomId) => {
    quittingRef.current = roomId;
    setQuittingGame(roomId);
    try {
      await api.post(`/rooms/${roomId}/quit`);
      toast.success(t("lobby.toast.quit"));
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      // Already quit or already removed — treat as success silently
      if (!detail.includes("déjà sorti") && !detail.includes("introuvable")) {
        toast.error(trServer(detail) || t("common.error"));
      }
    } finally {
      // Always remove from local list regardless of server response
      setActiveGames((prev) => prev.filter((g) => g.room_id !== roomId));
      quittingRef.current = null;
      setQuittingGame(null);
    }
  };

  const handleSolo = async (numPlayers, botDifficulty = form.bot_difficulty) => {
    try {
      const { data } = await api.post("/rooms/solo", {
        num_players: numPlayers,
        threshold_x: form.threshold_x,
        elim_score_y: form.elim_score_y,
        num_jokers: form.num_jokers,
        ...effectivePenalties(),
        require_pure_sequence: toggles.pureSequence,
        bot_difficulty: botDifficulty,
      });
      toast.success(t("lobby.toast.solo", { n: numPlayers - 1 }));
      navigate(`/game/${data.room.room_id}`);
    } catch (e) {
      toast.error(trServer(e?.response?.data?.detail) || t("lobby.toast.create"));
    }
  };

  const openTutorial = () => {
    setTutoOpen(true);
    if (!tutoSeen) markTutoSeen();
  };
  const tapisWasSeen = () => {
    if (tapisSeen) return;
    setTapisSeen(true);
    writeLocal(TAPIS_SEEN, "1");
  };
  const busy = activeGames.length > 0;
  const badges = { social: invites.length, table: !tapisSeen };

  const debugPanel = DEBUG_SCENARIOS && scenarios.length > 0 && (
    <div className="mt-6 border border-amber-400/20 rounded-2xl p-5 bg-amber-950/10">
      <div className="flex items-center gap-2 mb-4 text-amber-300">
        <FlaskConical className="h-4 w-4" />
        <span className="font-semibold text-sm uppercase tracking-wide">Scénarios de debug</span>
      </div>
      <div className="space-y-3">
        {scenarios.map((s) => (
          <div key={s.id} className="flex items-start justify-between gap-4 bg-slate-800/40 rounded-xl p-3">
            <div className="min-w-0">
              <div className="text-white font-medium text-sm">{s.label}</div>
              <div className="text-slate-400 text-xs mt-0.5 whitespace-pre-line">{s.description}</div>
              <div className="text-amber-400/70 text-[11px] mt-1 font-mono">{s.hint}</div>
            </div>
            <Button
              size="sm"
              disabled={debugLoading === s.id}
              onClick={() => handleDebugScenario(s.id)}
              className="shrink-0 bg-amber-600 hover:bg-amber-500 text-slate-950 rounded-full font-semibold text-xs"
            >
              {debugLoading === s.id ? "…" : "Lancer"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="relative isolate min-h-screen bg-slate-950">
      <BrandBackground fixed dim />
      {/* The ad's band (apps only): the native banner (320 × 50, centred) is drawn over the top
          of the web view. The band stays put while the page scrolls under it, its room is kept in
          the flow from the start, and it shows the player's felt when no ad comes (as in the game). */}
      {bannerHeight > 0 && (
        <>
          <div aria-hidden="true" style={{ height: bannerHeight + 1 }} />
          <div
            aria-hidden="true"
            className="rami-ad-slot fixed inset-x-0 top-0 z-40 border-b border-white/5"
            style={{ ...matStyle(tableThemeId), height: bannerHeight + 1 }}
          />
        </>
      )}

      <LobbyHeader
        user={user}
        rank={rank}
        top={bannerHeight > 0 ? bannerHeight + 1 : 0}
        tab={tab}
        onTab={goTab}
        badges={badges}
        onProfile={() => goTab("profile")}
        onSettings={() => setSettingsOpen(true)}
      />

      <main className="max-w-5xl mx-auto px-3 sm:px-6 pt-3 pb-24 lg:pb-10">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
          >
            {tab === "play" && (
              <PlayTab
                themeId={tableThemeId}
                activeGames={activeGames}
                quitting={quittingGame}
                onResume={(id) => navigate(`/game/${id}`)}
                onQuit={handleQuitGame}
                invite={invites[0]}
                inviteCount={invites.length}
                onAnswerInvite={answer}
                inviteActing={invites[0] && acting[invites[0].id]}
                inviteError={invites[0] && errors[invites[0].id]}
                onlineRooms={onlineRooms}
                matchmaking={matchmakingLoading}
                onPlayOnline={handlePlayOnline}
                onJoinOnline={handleJoin}
                onRefresh={refreshLists}
                refreshing={refreshing}
                rooms={rooms}
                onCreate={openCreateDialog}
                onJoinRoom={handleJoin}
                level={form.bot_difficulty}
                onLevel={setSoloLevel}
                onSolo={(n) => handleSolo(n)}
                tutorialSeen={tutoSeen}
                onTutorial={openTutorial}
              >
                {debugPanel}
              </PlayTab>
            )}
            {tab === "rank" && <RankTab rank={rank} />}
            {tab === "social" && (
              <SocialTab invites={invites} onAnswerInvite={answer} inviteActing={acting} inviteErrors={errors} busy={busy} />
            )}
            {tab === "table" && <TableTab onSeen={tapisWasSeen} />}
            {tab === "profile" && <ProfileTab rank={rank} />}
          </motion.div>
        </AnimatePresence>
      </main>

      <LobbyTabBar tab={tab} onTab={goTab} badges={badges} />

      <SettingsSheet
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        user={user}
        onLogout={async () => {
          await logout();
          navigate("/");
        }}
      />
      <TutorialDialog
        open={tutoOpen}
        onOpenChange={setTutoOpen}
        onPractice={() => handleSolo(2, "debutant")}
        practiceDisabled={busy}
      />
      <CreateRoomDialog
        open={createOpen}
        onOpenChange={(v) => { if (!busy || !v) setCreateOpen(v); }}
        form={form}
        setForm={setForm}
        toggles={toggles}
        setToggles={setToggles}
        onCreate={handleCreate}
      />
    </div>
  );
}
